import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { formatCurrency } from "@/lib/currency";
import { stripLegacyLabel } from "@/lib/legacy-label";
import { safeFormatDate } from "@/lib/dates";
import { FileDown, Loader2, Link2Off } from "lucide-react";
import { toast } from "sonner";

/**
 * PUBLIC, no-login horse ranking report.
 *
 * Opened via a 7-day share link (`/share/horse/:token`). Renders a
 * read-only "paper" report that mirrors the downloadable PDF style
 * (warm-white canvas, dark logo, brass eyebrow, Cormorant title, Plex
 * Mono numbers, iMessage-style photo cluster) and offers a Download PDF
 * button that reuses the exact same generator as the in-app report.
 *
 * The palette is hard-coded to the light "paper" tokens so the page
 * looks identical regardless of the visitor's system dark/light mode —
 * this is a shareable artefact, not an app surface.
 */

// Paper-mode brand tokens (mirror pdfReport.js / index.css :root).
const C = {
  paper: "#f7f4ed",
  ink: "#171511",
  inkSoft: "#4a463d",
  muted: "#6f6a5e",
  faint: "#8a8272",
  brass: "#a8823c",
  hairline: "#e6e0d2",
  hairlineSoft: "#eee8da",
  card: "#fffdf8",
};

const METRICS = [
  ["stride_comfort", "Stride comfort"],
  ["balance", "Balance overall"],
  ["balance_left", "Balance left"],
  ["balance_right", "Balance right"],
  ["agility", "Agility in turns"],
  ["temperament", "Temperament"],
  ["top_speed", "Top speed"],
  ["acceleration", "Acceleration"],
  ["rideoff_strength", "Ride-off strength"],
  ["stopping", "Stopping power"],
  ["lead_changes", "Lead changes"],
  ["mouth_rein", "Mouth and rein"],
  ["env_reaction", "Environment reaction"],
  ["recovery", "Recovery"],
  ["overall", "Overall suitability"],
];

const serif = "'Cormorant Garamond', ui-serif, Georgia, serif";
const mono = "'IBM Plex Mono', ui-monospace, monospace";
const sans = "'Instrument Sans', ui-sans-serif, system-ui, sans-serif";

function photoUrl(horseId, p) {
  return p.photo_id && !String(p.photo_id).startsWith("legacy-")
    ? `/api/horses/${horseId}/photos/${p.photo_id}/card`
    : `/api/horses/${horseId}/photo/card`;
}

export default function PublicHorseReportPage() {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading", data: null, code: null });
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await publicApi.get(`/public/horse-report/${token}`);
        if (alive) setState({ status: "ok", data: res.data, code: 200 });
      } catch (e) {
        const code = e?.response?.status || 0;
        if (alive) setState({ status: "error", data: null, code });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const data = state.data;
  const h = data?.horse || {};
  const r = h.ranking || {};

  const avgs = useMemo(() => {
    const rated = (data?.chukkas || []).filter((c) => typeof c?.overall === "number");
    return METRICS.map(([key, label]) => {
      const vals = rated.map((c) => c?.[key]).filter((v) => typeof v === "number" && Number.isFinite(v));
      if (!vals.length) return { key, label, avg: null, n: 0 };
      return { key, label, avg: vals.reduce((a, b) => a + b, 0) / vals.length, n: vals.length };
    });
  }, [data]);

  const notes = useMemo(
    () => (data?.chukkas || []).filter((c) => (c?.writeup || c?.notes || "").trim().length > 0),
    [data],
  );

  const download = async () => {
    if (downloading || !data) return;
    setDownloading(true);
    try {
      const { downloadHorseReport } = await import("@/lib/pdfHorseReport");
      await downloadHorseReport({
        horse: h,
        rank: data.rank || undefined,
        total: data.total || undefined,
        chukkas: data.chukkas || [],
        photos: data.photos || [],
        canSeeHorseDetails: true,
      });
      toast.success("Report downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Could not generate the PDF");
    } finally {
      setDownloading(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────
  if (state.status === "loading") {
    return (
      <Shell>
        <div className="flex items-center justify-center py-32" data-testid="public-report-loading">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: C.brass }} />
        </div>
      </Shell>
    );
  }

  // ── Error / expired / revoked ────────────────────────────────────
  if (state.status === "error") {
    const gone = state.code === 410;
    return (
      <Shell>
        <div
          className="mx-auto max-w-md text-center py-24"
          data-testid="public-report-error"
        >
          <Link2Off className="mx-auto h-10 w-10" style={{ color: C.faint }} strokeWidth={1.5} />
          <h1 className="mt-6 text-3xl" style={{ fontFamily: serif, color: C.ink }}>
            {gone ? "This link is no longer active" : "Report not found"}
          </h1>
          <p className="mt-3 text-sm" style={{ color: C.muted, fontFamily: sans }}>
            {gone
              ? "The share link has expired or been revoked by the owner. Ask them to send a fresh one."
              : "We couldn't find a report for this link. Please double-check the address you were sent."}
          </p>
        </div>
      </Shell>
    );
  }

  const pool = r.pool || "Unrated";
  const forSale = h.status === "for_sale";
  const meta = [];
  if (data.rank && data.total) meta.push(`Rank ${data.rank} of ${data.total}`);
  meta.push(`${pool} pool`);
  if (r.stars) meta.push(`${r.stars} star${r.stars === 1 ? "" : "s"}`);
  meta.push(`${r.chukkas_count ?? 0} sessions`);

  const photos = (data.photos || []).slice(0, 4);

  const profile = [
    { label: "Age", value: h.age != null ? `${h.age} years` : "", mono: true },
    { label: "Sex", value: h.sex },
    { label: "Breed", value: h.breed },
    { label: "Colour", value: h.color },
    { label: "Height", value: h.height_hands ? `${h.height_hands} hands` : "", mono: true },
    { label: "Location", value: h.location },
    { label: "Class", value: h.computed_class },
    { label: "Work status", value: h.work_status },
    { label: "Time at farm", value: h.time_at_farm_days != null ? `${h.time_at_farm_days} days` : "", mono: true },
    { label: "Arrived", value: h.arrival_date, mono: true },
  ].filter((p) => cell(p.value));
  if (Number.isFinite(h.listed_price)) {
    profile.push({ label: "Asking price", value: formatCurrency(h.listed_price, "USD"), mono: true });
    profile.push({ label: "Sale status", value: forSale ? "For sale" : "Not for sale" });
  }

  return (
    <Shell>
      <div className="mx-auto max-w-4xl px-5 py-8 sm:py-14" data-testid="public-report">
        {/* Header row */}
        <div className="flex items-center justify-between gap-4 pb-6" style={{ borderBottom: `1.5px solid ${C.ink}` }}>
          <div className="flex items-center gap-3">
            <img src="/brand/logo-dark.png" alt="African Polistas" className="h-8 w-8 object-contain"
                 onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <span className="text-[11px] sm:text-xs tracking-[0.22em]" style={{ color: C.ink, fontFamily: sans }}>
              AFRICAN POLISTAS
            </span>
          </div>
          <button
            type="button"
            onClick={download}
            disabled={downloading}
            data-testid="public-report-download"
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-opacity disabled:opacity-60"
            style={{ background: C.ink, color: C.paper, fontFamily: sans }}
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
            Download PDF
          </button>
        </div>

        {/* Title + photo cluster */}
        <div className="grid gap-8 pt-10 sm:grid-cols-5">
          <div className="sm:col-span-3">
            <div className="text-[11px] font-semibold tracking-[0.26em]" style={{ color: C.brass, fontFamily: sans }}>
              RANKINGS &middot; HORSE REPORT
            </div>
            <h1 className="mt-3 leading-none" style={{ fontFamily: serif, color: C.ink, fontSize: "3.25rem" }}
                data-testid="public-report-name">
              {h.name || "Horse"}
            </h1>
            <p className="mt-4 text-base" style={{ color: C.inkSoft, fontFamily: sans }}>
              {meta.join("  \u00b7  ")}
            </p>
            <p className="mt-1 text-xs" style={{ color: C.muted, fontFamily: sans }}>
              Formula v9 score. Updated after every rated ride.
            </p>
          </div>
          <div className="sm:col-span-2">
            <PhotoCluster horseId={h.id} photos={photos} />
          </div>
        </div>

        {/* Summary band */}
        <div className="mt-10 grid grid-cols-3" style={{ borderTop: `1.5px solid ${C.ink}` }}>
          <Band label="Score" value={r.score != null ? Number(r.score).toFixed(1) : "—"} mono first />
          <Band label="Pool" value={pool} />
          <Band label="Rank" value={data.rank && data.total ? `#${data.rank} of ${data.total}` : "—"} mono />
        </div>

        {/* Score breakdown */}
        <Section title="Score breakdown" />
        <div className="space-y-3">
          <Bar label="Performance" value={r.performance} />
          <Bar label="Consistency" value={r.consistency} />
          <Bar label="Durability" value={r.durability} />
        </div>

        {/* Profile */}
        <Section title="Profile" />
        <div className="grid grid-cols-2 gap-x-8 gap-y-5 sm:grid-cols-3">
          {profile.map((p) => (
            <div key={p.label}>
              <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: C.muted, fontFamily: sans }}>
                {p.label}
              </div>
              <div className="mt-1 text-[15px]" style={{ color: C.ink, fontFamily: p.mono ? mono : sans }}>
                {cell(p.value)}
              </div>
            </div>
          ))}
        </div>

        {/* Metric averages */}
        {notes.length >= 0 && avgs.some((a) => a.avg != null) && (
          <>
            <Section title="Metric averages" />
            <div className="grid gap-x-10 gap-y-0 sm:grid-cols-2">
              {avgs.map((a) => (
                <div key={a.key} className="flex items-center justify-between py-2"
                     style={{ borderBottom: `0.5px solid ${C.hairline}` }}>
                  <span className="text-sm" style={{ color: C.inkSoft, fontFamily: sans }}>{a.label}</span>
                  <span className="text-sm tabular-nums" style={{ color: C.ink, fontFamily: mono }}>
                    {a.avg != null ? `${a.avg.toFixed(1)} / 5` : "—"}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Rider notes */}
        <Section title={`Rider notes \u00b7 ${notes.length}`} />
        {notes.length === 0 ? (
          <p className="text-sm" style={{ color: C.muted, fontFamily: sans }}>
            No rider notes recorded yet for this horse.
          </p>
        ) : (
          <div className="space-y-4" data-testid="public-report-notes">
            {notes.map((c, i) => (
              <div key={c.id || i} className="rounded-lg p-4"
                   style={{ background: C.card, border: `0.5px solid ${C.hairline}` }}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] uppercase tracking-[0.12em]"
                     style={{ color: C.muted, fontFamily: sans }}>
                  <span style={{ fontFamily: mono }}>{c.created_at ? safeFormatDate(c.created_at, "d MMM yyyy") : ""}</span>
                  {stripLegacyLabel(c.rider_name) && <><span>&middot;</span><span>{stripLegacyLabel(c.rider_name)}</span></>}
                  {stripLegacyLabel(c.competition_type) && <><span>&middot;</span><span>{stripLegacyLabel(c.competition_type)}</span></>}
                  {c.bit_used && <><span>&middot;</span><span>Bit: {c.bit_used}</span></>}
                  {typeof c.overall === "number" && (
                    <span className="ml-auto" style={{ fontFamily: mono, color: C.brass }}>{c.overall}/5</span>
                  )}
                </div>
                <p className="mt-2 text-[15px] leading-relaxed" style={{ color: C.ink, fontFamily: serif }}>
                  {(c.writeup || c.notes || "").trim()}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Videos (read-only playback) */}
        {(data.videos || []).length > 0 && (
          <>
            <Section title="Videos" />
            <div className="grid gap-4 sm:grid-cols-2" data-testid="public-report-videos">
              {(data.videos || []).map((v) => (
                <div key={v.video_id} className="overflow-hidden rounded-lg"
                     style={{ border: `0.5px solid ${C.hairline}` }} data-testid={`public-video-${v.video_id}`}>
                  <video src={v.url} controls preload="metadata" playsInline
                         className="w-full aspect-video" style={{ background: "#000" }} />
                  {v.title && (
                    <div className="px-3 py-2 text-sm" style={{ color: C.ink, fontFamily: sans }}>{v.title}</div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="mt-14 flex flex-wrap items-center justify-between gap-2 pt-5 text-[11px]"
             style={{ borderTop: `0.5px solid ${C.hairline}`, color: C.muted, fontFamily: sans }}>
          <span>Operations Report &middot; The Crags, Plettenberg Bay</span>
          {data.expires_at && (
            <span style={{ fontFamily: mono }}>
              Link expires {safeFormatDate(data.expires_at, "d MMM yyyy")}
            </span>
          )}
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen w-full" style={{ background: C.paper }}>
      {children}
    </div>
  );
}

function Section({ title }) {
  return (
    <div className="mb-5 mt-12">
      <div className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: C.brass, fontFamily: sans }}>
        {title}
      </div>
      <div className="mt-2" style={{ borderBottom: `1.5px solid ${C.ink}` }} />
    </div>
  );
}

function Band({ label, value, mono: isMono, first }) {
  return (
    <div className="px-1 py-5" style={{ borderLeft: first ? "none" : `0.5px solid ${C.hairline}` }}>
      <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: C.muted, fontFamily: sans }}>
        {label}
      </div>
      <div
        className="mt-2"
        style={{
          fontFamily: isMono ? mono : serif,
          color: C.ink,
          fontSize: isMono ? "clamp(1.05rem, 4.5vw, 1.6rem)" : "clamp(1.3rem, 5vw, 2rem)",
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Bar({ label, value }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="flex items-center gap-4">
      <span className="w-28 shrink-0 text-[10px] uppercase tracking-[0.14em]" style={{ color: C.muted, fontFamily: sans }}>
        {label}
      </span>
      <div className="h-[6px] flex-1 rounded-full" style={{ background: C.hairline }}>
        <div className="h-[6px] rounded-full" style={{ width: `${v}%`, background: C.brass }} />
      </div>
      <span className="w-10 text-right text-sm tabular-nums" style={{ color: C.ink, fontFamily: mono }}>
        {v.toFixed(0)}
      </span>
    </div>
  );
}

/**
 * iMessage-style photo stack. Older photos fan out behind; the most
 * recent (index 0) sits on top, upright and wholly visible.
 */
function PhotoCluster({ horseId, photos }) {
  if (!photos || photos.length === 0) {
    return (
      <div className="flex aspect-square items-center justify-center rounded-xl"
           style={{ background: C.card, border: `0.5px solid ${C.hairline}` }}>
        <span className="text-xs" style={{ color: C.faint, fontFamily: sans }}>No photos</span>
      </div>
    );
  }
  const layouts = {
    1: [{ rot: -1.5, x: 0, y: 0 }],
    2: [{ rot: -2, x: 0, y: 0 }, { rot: 8, x: -14, y: -6 }],
    3: [{ rot: -2, x: 0, y: 0 }, { rot: 9, x: -16, y: -5 }, { rot: -10, x: 16, y: -8 }],
    4: [{ rot: -2, x: 0, y: 2 }, { rot: 9, x: -18, y: -4 }, { rot: -11, x: 18, y: -6 }, { rot: 4, x: -3, y: -12 }],
  };
  const layout = layouts[Math.min(photos.length, 4)];
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[280px]" data-testid="public-report-photos">
      {photos.map((p, i) => {
        const L = layout[i];
        // Render oldest first (higher z for newer). Newest = index 0 on top.
        const z = photos.length - i;
        return (
          <div
            key={p.photo_id || i}
            className="absolute left-1/2 top-1/2 h-[74%] w-[74%] overflow-hidden rounded-md bg-white"
            style={{
              transform: `translate(-50%, -50%) translate(${L.x}px, ${L.y}px) rotate(${L.rot}deg)`,
              zIndex: z,
              padding: 6,
              boxShadow: "0 10px 24px rgba(23,21,17,0.22)",
            }}
          >
            <img
              src={photoUrl(horseId, p)}
              alt=""
              className="h-full w-full rounded-sm object-cover"
              onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
            />
          </div>
        );
      })}
    </div>
  );
}

function cell(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (s === "-" || s.toLowerCase() === "n/a") return "";
  return s;
}
